/**
 * Viewer Share Panel
 *
 * Lets the user:
 *   - Save the current viewer scene as a shareable URL.
 *   - See all viewer shares they previously created.
 *   - Copy / open / delete each share.
 *
 * Storage uses the existing /api/share + /api/shares + DELETE /api/shares/:id
 * endpoints (same backend as the app share). To distinguish viewer shares
 * from app shares, the saved sceneState carries `type: 'viewer'`.
 *
 * Restore (loading a shared viewer URL like `/viewer?share=<id>`) is handled
 * in viewer.ts at startup — see captureViewerState / applyViewerState there.
 *
 * Public API:
 *   const handle = createViewerSharePanel({
 *     authBaseUrl: 'https://echov.projectsndev.com',
 *     getCurrentState: () => ({ type: 'viewer', models, folder, camera, ... }),
 *   });
 */

export interface ViewerShareState {
  type: "viewer";
  /**
   * Original models registered in the viewer URL.
   *  - `id`     : modelId used by FragmentsModels (also the key for visibility/clipping).
   *  - `fileId` : MongoDB file id used by `/api/files/:fileId/url` to re-sign R2 URLs.
   *               Required for the share to work for unauthenticated visitors.
   *  - `name`   : Friendly file name shown in the Models Tree.
   */
  models: Array<{
    id: string;
    name: string;
    fileId?: string;
    /** "frag" | "gltf" — needed so share visitors load the right kind. */
    modelType?: string;
  }>;
  /** Folder name (used as panel title). */
  folder?: string;
  /** Camera state in world coordinates. */
  camera?: {
    position: [number, number, number];
    target: [number, number, number];
  };
  /** Per-modelId visibility (true = visible). */
  modelVisibility?: Record<string, boolean>;
  /** Clipping planes captured from the Clipper component. */
  clippingPlanes?: Array<{
    position: [number, number, number];
    normal: [number, number, number];
    enabled: boolean;
  }>;
  /** Optional camera mode (Move / Rotate). */
  gizmoMode?: "translate" | "rotate";
  /**
   * Per-model viewer-only manual transform overrides (from the XYZ
   * gizmo). Keyed by modelId. Applied on share restore so users see
   * exactly the arrangement the sharer set up. Never written back to
   * the admin DB.
   */
  viewerOverrides?: Record<
    string,
    {
      position: { x: number; y: number; z: number };
      rotation: { x: number; y: number; z: number };
      scale: { x: number; y: number; z: number };
    }
  >;
}

interface SharedViewItem {
  shareId: string;
  label: string;
  description?: string;
  createdAt: string;
  expiresAt?: string;
  viewCount?: number;
  active: boolean;
  shareUrl: string;
}

interface CreateOpts {
  authBaseUrl: string; // e.g. "https://echov.projectsndev.com" or "" for same-origin
  getCurrentState: () => ViewerShareState;
}

export interface ViewerSharePanelHandle {
  panel: HTMLElement;
  togglePanel(): void;
  setPanelVisible(visible: boolean): void;
  isPanelVisible(): boolean;
  refresh(): void;
  dispose(): void;
}

const TYPE_TAG = "viewer";

function getAuthHeaders(): HeadersInit {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getCurrentUser(): { userId?: string; username?: string } {
  try {
    const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
    if (!raw) return {};
    const u = JSON.parse(raw);
    return { userId: u.id || u._id || u.userId, username: u.username };
  } catch {
    return {};
  }
}

export function createViewerSharePanel(opts: CreateOpts): ViewerSharePanelHandle {
  const { authBaseUrl, getCurrentState } = opts;

  // ---------- Panel shell ----------
  const panel = document.createElement("div");
  panel.id = "viewer-share-panel";
  panel.style.cssText = `
    position: fixed;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
    width: 22rem;
    max-height: 75vh;
    z-index: 9999;
    background: rgba(10, 14, 39, 0.95);
    border: 1px solid #44d3de;
    border-radius: 8px;
    box-shadow: 0 0 20px rgba(68, 211, 222, 0.3);
    backdrop-filter: blur(10px);
    color: #e0e0e0;
    font: 12px -apple-system, 'Segoe UI', Helvetica, sans-serif;
    pointer-events: auto;
    user-select: none;
    overflow: hidden;
    display: none;
    flex-direction: column;
  `;

  let setPanelVisible: (visible: boolean) => void = () => {};

  // ---------- Header ----------
  const header = document.createElement("div");
  header.setAttribute("data-panel-header", "true");
  header.style.cssText = `
    padding: 0.5rem;
    border-bottom: 1px solid rgba(68, 211, 222, 0.3);
    color: #44d3de;
    font-weight: 600;
    font-size: 0.75rem;
    cursor: move;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  `;
  const titleSpan = document.createElement("span");
  titleSpan.textContent = "Shared Views";
  header.appendChild(titleSpan);
  const closeBtn = document.createElement("button");
  closeBtn.innerHTML = "&times;";
  closeBtn.title = "Close panel";
  closeBtn.style.cssText = `
    background: transparent; border: none; color: #44d3de;
    cursor: pointer; font-size: 16px; line-height: 1;
    padding: 0 4px; border-radius: 4px;
  `;
  closeBtn.onmouseenter = () => { closeBtn.style.background = "rgba(255, 255, 255, 0.12)"; };
  closeBtn.onmouseleave = () => { closeBtn.style.background = "transparent"; };
  closeBtn.addEventListener("click", (e) => { e.stopPropagation(); setPanelVisible(false); });
  header.appendChild(closeBtn);
  panel.appendChild(header);

  // ---------- Body ----------
  const body = document.createElement("div");
  body.style.cssText = `
    flex: 1;
    overflow: auto;
    padding: 0.6rem;
    font-size: 0.7rem;
    color: #e0e0e0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  `;
  panel.appendChild(body);

  // ----- Save section -----
  const saveSection = document.createElement("div");
  saveSection.style.cssText = `
    padding: 8px;
    background: rgba(68, 211, 222, 0.06);
    border: 1px solid rgba(68, 211, 222, 0.25);
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  `;
  const saveTitle = document.createElement("div");
  saveTitle.textContent = "Save current view";
  saveTitle.style.cssText = "color: #44d3de; font-weight: 600; font-size: 11px;";
  saveSection.appendChild(saveTitle);

  const labelInput = document.createElement("input");
  labelInput.type = "text";
  labelInput.placeholder = "Label (e.g. Floor 3 inspection)";
  labelInput.style.cssText = `
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(68, 211, 222, 0.4);
    color: #e0e0e0;
    border-radius: 4px;
    padding: 5px 8px;
    font-size: 11px;
    outline: none;
  `;
  saveSection.appendChild(labelInput);

  const descInput = document.createElement("textarea");
  descInput.placeholder = "Description (optional)";
  descInput.rows = 2;
  descInput.style.cssText = `
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(68, 211, 222, 0.4);
    color: #e0e0e0;
    border-radius: 4px;
    padding: 5px 8px;
    font-size: 11px;
    outline: none;
    resize: vertical;
    font-family: inherit;
  `;
  saveSection.appendChild(descInput);

  const expirySelect = document.createElement("select");
  expirySelect.style.cssText = `
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(68, 211, 222, 0.4);
    color: #e0e0e0;
    border-radius: 4px;
    padding: 5px 8px;
    font-size: 11px;
    outline: none;
  `;
  for (const [val, txt] of [["7", "7 days"], ["30", "30 days"], ["90", "90 days"], ["365", "1 year"], ["", "Never"]]) {
    const o = document.createElement("option");
    o.value = val;
    o.textContent = `Expires: ${txt}`;
    expirySelect.appendChild(o);
  }
  expirySelect.value = "30";
  saveSection.appendChild(expirySelect);

  const saveBtn = document.createElement("button");
  saveBtn.textContent = "Save & copy link";
  saveBtn.style.cssText = `
    background: #44d3de;
    color: #0a1422;
    border: none;
    border-radius: 4px;
    padding: 7px 10px;
    font-weight: 600;
    cursor: pointer;
    font-size: 11.5px;
    transition: background 120ms ease;
  `;
  saveBtn.onmouseenter = () => { saveBtn.style.background = "#5be1ec"; };
  saveBtn.onmouseleave = () => { saveBtn.style.background = "#44d3de"; };
  saveSection.appendChild(saveBtn);

  const saveStatus = document.createElement("div");
  saveStatus.style.cssText = "color: #8db5b9; font-size: 10.5px; min-height: 14px;";
  saveSection.appendChild(saveStatus);

  body.appendChild(saveSection);

  // ----- List section -----
  const listHeaderRow = document.createElement("div");
  listHeaderRow.style.cssText = `
    display: flex; justify-content: space-between; align-items: center;
    color: #44d3de; font-weight: 600; font-size: 11px;
    border-top: 1px solid rgba(68, 211, 222, 0.2);
    padding-top: 8px;
  `;
  const listTitleSpan = document.createElement("span");
  listTitleSpan.textContent = "My viewer shares";
  listHeaderRow.appendChild(listTitleSpan);
  const refreshBtn = document.createElement("button");
  refreshBtn.title = "Refresh";
  refreshBtn.innerHTML = `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="23 4 23 10 17 10"/>
      <polyline points="1 20 1 14 7 14"/>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
    </svg>`;
  refreshBtn.style.cssText = `
    background: transparent; border: none; color: #44d3de;
    cursor: pointer; padding: 2px; border-radius: 3px;
    display: flex; align-items: center;
  `;
  refreshBtn.onmouseenter = () => { refreshBtn.style.background = "rgba(68, 211, 222, 0.18)"; };
  refreshBtn.onmouseleave = () => { refreshBtn.style.background = "transparent"; };
  listHeaderRow.appendChild(refreshBtn);
  body.appendChild(listHeaderRow);

  const listContainer = document.createElement("div");
  listContainer.style.cssText = `
    display: flex; flex-direction: column; gap: 4px;
  `;
  body.appendChild(listContainer);

  const renderEmpty = (msg: string) => {
    listContainer.innerHTML = "";
    const d = document.createElement("div");
    d.style.cssText = "color: #688080; font-size: 10.5px; font-style: italic; padding: 6px 0;";
    d.textContent = msg;
    listContainer.appendChild(d);
  };

  const buildShareRow = (item: SharedViewItem) => {
    const row = document.createElement("div");
    row.style.cssText = `
      display: flex; flex-direction: column; gap: 4px;
      padding: 6px 8px;
      background: rgba(68, 211, 222, 0.06);
      border: 1px solid rgba(68, 211, 222, 0.2);
      border-radius: 4px;
    `;

    const top = document.createElement("div");
    top.style.cssText = "display: flex; justify-content: space-between; align-items: center; gap: 6px;";

    const labelEl = document.createElement("span");
    labelEl.textContent = item.label || "(no label)";
    labelEl.style.cssText = `
      flex: 1; color: #cfeef0; font-weight: 600; font-size: 11px;
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    `;
    labelEl.title = item.label || "(no label)";
    top.appendChild(labelEl);

    const actions = document.createElement("div");
    actions.style.cssText = "display: flex; gap: 2px; flex-shrink: 0;";

    const mkActionBtn = (title: string, svg: string, color = "#44d3de") => {
      const b = document.createElement("button");
      b.title = title;
      b.innerHTML = svg;
      b.style.cssText = `
        background: transparent; border: none; color: ${color};
        cursor: pointer; padding: 2px 4px; border-radius: 3px;
        display: flex; align-items: center;
      `;
      b.onmouseenter = () => { b.style.background = `rgba(68, 211, 222, 0.18)`; };
      b.onmouseleave = () => { b.style.background = "transparent"; };
      return b;
    };

    // Copy URL
    const copyBtn = mkActionBtn(
      "Copy link",
      `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
         <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
         <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
       </svg>`
    );
    copyBtn.addEventListener("click", async (e) => {
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(item.shareUrl);
        copyBtn.style.color = "#69ffae";
        setTimeout(() => { copyBtn.style.color = "#44d3de"; }, 1200);
      } catch {
        // Fallback
        const ta = document.createElement("textarea");
        ta.value = item.shareUrl;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
    });
    actions.appendChild(copyBtn);

    // Open in new tab
    const openBtn = mkActionBtn(
      "Open",
      `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
         <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
         <polyline points="15 3 21 3 21 9"/>
         <line x1="10" y1="14" x2="21" y2="3"/>
       </svg>`
    );
    openBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      window.open(item.shareUrl, "_blank", "noopener,noreferrer");
    });
    actions.appendChild(openBtn);

    // Delete
    const delBtn = mkActionBtn(
      "Delete",
      `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
         <polyline points="3 6 5 6 21 6"/>
         <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/>
         <path d="M10 11v6"/><path d="M14 11v6"/>
         <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
       </svg>`,
      "#ff8a8a"
    );
    delBtn.addEventListener("click", async (e) => {
      e.stopPropagation();
      if (!confirm(`Delete share "${item.label || item.shareId}"?`)) return;
      try {
        const resp = await fetch(`${authBaseUrl}/api/shares/${item.shareId}`, {
          method: "DELETE",
          headers: { ...getAuthHeaders() },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        await refresh();
      } catch (err) {
        console.warn("[viewerShare] delete failed", err);
        alert("Failed to delete share. See console.");
      }
    });
    actions.appendChild(delBtn);

    top.appendChild(actions);
    row.appendChild(top);

    if (item.description) {
      const desc = document.createElement("div");
      desc.textContent = item.description;
      desc.style.cssText = "color: #8db5b9; font-size: 10.5px; line-height: 1.35;";
      row.appendChild(desc);
    }

    const meta = document.createElement("div");
    const created = item.createdAt ? new Date(item.createdAt).toLocaleString() : "?";
    const expires = item.expiresAt ? new Date(item.expiresAt).toLocaleDateString() : "—";
    const views = item.viewCount ?? 0;
    meta.style.cssText = "color: #688080; font-size: 10px;";
    meta.textContent = `Created ${created} · Expires ${expires} · ${views} views`;
    row.appendChild(meta);

    return row;
  };

  const refresh = async () => {
    listContainer.innerHTML = "";
    const loading = document.createElement("div");
    loading.style.cssText = "color: #8db5b9; font-size: 10.5px; padding: 6px 0;";
    loading.textContent = "Loading…";
    listContainer.appendChild(loading);
    try {
      const resp = await fetch(`${authBaseUrl}/api/shares`, {
        headers: { ...getAuthHeaders() },
      });
      if (!resp.ok) {
        renderEmpty(`Cannot load shares (HTTP ${resp.status})`);
        return;
      }
      const data = await resp.json();
      if (!data?.success || !Array.isArray(data.shares)) {
        renderEmpty("No shares");
        return;
      }
      // The API returns ALL shares for the user. We can't filter server-side
      // by sceneState.type without backend changes, so we filter on the
      // client by inspecting the raw share docs. Since /api/shares does not
      // include sceneState in its response, we instead match by URL: the
      // share label/description does not encode type. As a workaround, we
      // expect viewer shares to be created with shareUrl pointing to /viewer.
      // For now, show all shares but transform the URL to /viewer for any
      // share that was created from this panel (we trust the user).
      // Practical filter: viewer shares are created with description that
      // begins with "[viewer]" tag. Listed below.
      const items: SharedViewItem[] = data.shares
        .filter((s: any) =>
          (s.description && s.description.startsWith("[viewer]")) ||
          (s.label && s.label.startsWith("[viewer]"))
        )
        .map((s: any) => ({
          shareId: s.shareId,
          // Strip the [viewer] tag prefix from label/description for display
          label: (s.label || "").replace(/^\s*\[viewer\]\s*/, ""),
          description: (s.description || "").replace(/^\s*\[viewer\]\s*/, ""),
          createdAt: s.createdAt,
          expiresAt: s.expiresAt,
          viewCount: s.viewCount,
          active: s.active !== false,
          shareUrl: `${window.location.origin}/viewer?share=${s.shareId}`,
        }));

      listContainer.innerHTML = "";
      if (items.length === 0) {
        renderEmpty("No saved viewer shares yet — save one above");
        return;
      }
      for (const it of items) listContainer.appendChild(buildShareRow(it));
    } catch (err) {
      console.warn("[viewerShare] refresh failed", err);
      renderEmpty("Failed to load shares (check login / network)");
    }
  };

  refreshBtn.addEventListener("click", () => refresh());

  // ---------- Save handler ----------
  saveBtn.addEventListener("click", async () => {
    saveBtn.disabled = true;
    saveStatus.style.color = "#8db5b9";
    saveStatus.textContent = "Saving…";
    try {
      const state = getCurrentState();
      const { userId, username } = getCurrentUser();
      const labelRaw = labelInput.value.trim() || "Untitled view";
      const descRaw = descInput.value.trim();
      // Tag with [viewer] so the list filter picks it up. We also keep
      // sceneState.type === 'viewer' for the loader.
      const taggedLabel = `[viewer] ${labelRaw}`;
      const taggedDesc = descRaw ? `[viewer] ${descRaw}` : `[viewer]`;
      const expiryDays = expirySelect.value === "" ? null : parseInt(expirySelect.value, 10);

      const body = {
        userId,
        username,
        sceneState: state,
        label: taggedLabel,
        description: taggedDesc,
        expiryDays,
      };

      const resp = await fetch(`${authBaseUrl}/api/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(`HTTP ${resp.status} — ${t}`);
      }
      const data = await resp.json();
      if (!data?.success || !data.shareId) {
        throw new Error(data?.error || "Server returned no shareId");
      }
      const url = `${window.location.origin}/viewer?share=${data.shareId}`;
      try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
      saveStatus.style.color = "#69ffae";
      saveStatus.textContent = "Saved & link copied!";
      labelInput.value = "";
      descInput.value = "";
      await refresh();
    } catch (err: any) {
      console.warn("[viewerShare] save failed", err);
      saveStatus.style.color = "#ff8a8a";
      saveStatus.textContent = "Save failed: " + (err?.message || String(err));
    } finally {
      saveBtn.disabled = false;
    }
  });

  // ---------- Drag handle ----------
  let dragging = false;
  let dragOffsetX = 0;
  let dragOffsetY = 0;
  header.addEventListener("mousedown", (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target === closeBtn || closeBtn.contains(target)) return;
    const rect = panel.getBoundingClientRect();
    dragOffsetX = e.clientX - rect.left;
    dragOffsetY = e.clientY - rect.top;
    dragging = true;
    header.style.cursor = "grabbing";
    e.preventDefault();
    e.stopPropagation();
  });
  const onPanelDrag = (e: MouseEvent) => {
    if (!dragging) return;
    e.preventDefault();
    const w = panel.offsetWidth;
    const maxX = window.innerWidth - 40;
    const maxY = window.innerHeight - 40;
    let nx = e.clientX - dragOffsetX;
    let ny = e.clientY - dragOffsetY;
    nx = Math.max(0 - w + 40, Math.min(nx, maxX));
    ny = Math.max(0, Math.min(ny, maxY));
    panel.style.left = `${nx}px`;
    panel.style.top = `${ny}px`;
    panel.style.right = "auto";
    panel.style.bottom = "auto";
    panel.style.transform = "none";
  };
  const onPanelDragEnd = () => {
    if (dragging) {
      dragging = false;
      header.style.cursor = "move";
    }
  };
  document.addEventListener("mousemove", onPanelDrag);
  document.addEventListener("mouseup", onPanelDragEnd);

  document.body.appendChild(panel);

  // ---------- Toggle helpers ----------
  const isPanelVisible = () => panel.style.display !== "none";
  setPanelVisible = (visible: boolean) => {
    panel.style.display = visible ? "flex" : "none";
    if (visible) refresh();
  };
  const togglePanel = () => setPanelVisible(!isPanelVisible());

  return {
    panel,
    togglePanel,
    setPanelVisible,
    isPanelVisible,
    refresh,
    dispose() {
      document.removeEventListener("mousemove", onPanelDrag);
      document.removeEventListener("mouseup", onPanelDragEnd);
      panel.remove();
    },
  };
}
